<script lang="ts" setup>
import { ref, PropType, computed, watch } from "vue";
import {
  useDeleteDocumentSpaceConfigLinkMutation,
  useUpdateDocumentSpaceLinkPositionMutation,
} from "@/graphql/operations";
import { useFlashMessage, useDeleteConfirmation } from "@/hooks";
import { useForm, useField } from "vee-validate";
import { string } from "yup";
import { type Config } from "./Config";
import type { TemplateEditorLink } from "./types";
import { isValidDocumentSpaceConfigLinkUrl } from "@/util/documentSpaceConfigLinkUrl";

const props = defineProps({
  link: {
    type: Object as PropType<TemplateEditorLink | null>,
    default: null,
  },
  config: {
    type: Object as PropType<Config>,
    required: true,
  },
  links: {
    type: Array as PropType<Array<TemplateEditorLink | null>>,
    required: false,
    default: () => [],
  },
  currentLanguage: {
    type: String,
    required: true,
  },
});

const emits = defineEmits(["close", "reloadData", "newItem", "changeState"]);
const close = () => {
  emits("close");
};

const { validate, errors } = useForm();

const translation = computed(() => {
  if (!props.link) {
    return null;
  }
  return props.link.documentSpaceLinkTranslationsByLinkId?.nodes?.find(
    (t) => t!.languageCode === props.currentLanguage,
  );
});

const linkData = computed(() => {
  if (!props.link) {
    return {
      linkId: undefined,
      translationId: undefined,
      url: "",
      sectionTitle: "",
      title: "",
      isMandatory: true,
    };
  }

  const translationValue = translation.value;

  return {
    linkId: props.link.id,
    translationId: translationValue?.id,
    url: translationValue ? translationValue.url : (props.link.url ?? ""),
    sectionTitle: translationValue
      ? translationValue.sectionTitle
      : (props.link.sectionTitle ?? ""),
    title: translationValue ? translationValue.title : (props.link.title ?? ""),
    isMandatory: props.link.isMandatory,
  };
});

const { value: url } = useField<string>(
  "url",
  string()
    .required()
    .test(
      "document-space-config-link-url",
      "this must be a valid URL",
      (value) => !value || isValidDocumentSpaceConfigLinkUrl(value),
    ),
  {
    initialValue: linkData.value.url,
  },
);

const { value: title } = useField<string>("title", string().required(), {
  initialValue: linkData.value.title,
});

const sectionTitle = ref<string | undefined | null>(
  linkData.value.sectionTitle,
);
const isMandatory = ref<boolean>(linkData.value.isMandatory);

watch(
  () => props.link,
  (newLink, oldLink) => {
    if (oldLink?.id !== newLink?.id) {
      sectionTitle.value = linkData.value.sectionTitle;
      title.value = linkData.value.title;
      url.value = linkData.value.url;
      isMandatory.value = linkData.value.isMandatory;
    }
  },
);

watch(
  () => props.currentLanguage,
  () => {
    url.value = linkData.value.url;
    sectionTitle.value = linkData.value.sectionTitle;
    title.value = linkData.value.title;
  },
);

const { displayFlashMessage } = useFlashMessage();
const { displayDeleteConfirmation } = useDeleteConfirmation();

const orderedLinks = computed<TemplateEditorLink[]>(() => {
  const filtered = (props.links ?? []).filter(
    (link): link is TemplateEditorLink => Boolean(link),
  );

  return filtered.sort((a, b) => {
    const bPos = Number(b.position ?? 0);
    const aPos = Number(a.position ?? 0);
    return bPos - aPos;
  });
});

const currentIndex = computed(() =>
  orderedLinks.value.findIndex((link) => link.id === props.link?.id),
);

const canReorder = computed(
  () =>
    props.currentLanguage === "default" &&
    !props.config.isTranslation &&
    Boolean(props.link?.id) &&
    orderedLinks.value.length > 1,
);

const canMoveUp = computed(
  () =>
    canReorder.value &&
    currentIndex.value >= 0 &&
    currentIndex.value < orderedLinks.value.length - 1,
);
const canMoveDown = computed(() => canReorder.value && currentIndex.value > 0);

const hasChanges = computed(
  () =>
    sectionTitle.value !== linkData.value?.sectionTitle ||
    title.value !== linkData.value?.title ||
    url.value !== linkData.value?.url ||
    isMandatory.value !== linkData.value.isMandatory,
);

watch(hasChanges, () => {
  emits("changeState", hasChanges.value);
});

const { executeMutation: executeUpdatePosition } =
  useUpdateDocumentSpaceLinkPositionMutation();

const reordering = ref(false);

const calculateNewPosition = (
  targetIndex: number,
  sourceIndex: number,
): number | null => {
  const list = orderedLinks.value;

  if (sourceIndex < 0 || list.length === 0) {
    return null;
  }

  const getPosition = (idx: number) => Number(list[idx]?.position ?? 0);

  let position: number;

  if (targetIndex === 0) {
    position = getPosition(0) + 1;
  } else if (targetIndex === list.length) {
    position = getPosition(list.length - 1) - 1;
  } else if (targetIndex > sourceIndex) {
    position = (getPosition(targetIndex - 1) + getPosition(targetIndex)) / 2;
  } else {
    position = (getPosition(targetIndex) + getPosition(targetIndex - 1)) / 2;
  }

  return position;
};

const changeOrder = async (direction: "up" | "down") => {
  if (!props.link?.id || !canReorder.value || reordering.value) {
    return;
  }

  const sourceIndex = currentIndex.value;
  if (sourceIndex < 0) {
    return;
  }

  const targetIndex = direction === "up" ? sourceIndex - 1 : sourceIndex + 2;

  const newPosition = calculateNewPosition(targetIndex, sourceIndex);

  if (newPosition === null) {
    return;
  }

  reordering.value = true;

  try {
    const result = await executeUpdatePosition({
      id: props.link.id,
      position: newPosition,
    });

    if (result.error) {
      throw new Error(result.error.message);
    }

    displayFlashMessage("Success", "success");
    emits("reloadData");
  } catch (err) {
    displayFlashMessage(err as string, "error");
  } finally {
    reordering.value = false;
  }
};

const submit = async () => {
  const validationResult = await validate();
  if (!validationResult.valid) {
    console.log("invalid data");
    return;
  }

  try {
    const linkId = await props.config.createOrUpdateLink(
      linkData.value.linkId,
      linkData.value.translationId,
      sectionTitle.value,
      title.value,
      url.value,
      isMandatory.value,
    );

    displayFlashMessage("Success", "success");
    if (!linkData.value.linkId) {
      emits("newItem", linkId);
    } else {
      emits("reloadData");
    }
  } catch (err) {
    displayFlashMessage(err as string, "error");
  }
};

const { executeMutation: deleteLink } =
  useDeleteDocumentSpaceConfigLinkMutation();
const displayConfirmation = async () => {
  if (!props.link?.id) {
    return;
  }

  const confirmed = await displayDeleteConfirmation(
    "templateEditor.toolbar.link.delete",
  );

  if (confirmed) {
    const res = await deleteLink({ linkId: props.link.id });
    if (res.error) {
      displayFlashMessage(res.error.message, "error");
    } else {
      displayFlashMessage("Success", "success");
    }
  }

  emits("reloadData");
  emits("close");
};
</script>

<template>
  <div class="toolbar_body">
    <div class="row">
      <div class="label_container">
        <label>{{
          $t("templateEditor.toolbar.link.sectionTitle.label")
        }}</label>
      </div>
      <div>
        <input type="text" class="input" v-model="sectionTitle" />
      </div>
    </div>
    <div class="row">
      <div class="label_container">
        <label>{{ $t("templateEditor.toolbar.link.linkTitle.label") }}</label>
      </div>
      <div>
        <input
          type="text"
          class="input"
          :class="{ invalid: errors.title !== undefined }"
          v-model="title"
        />
        <p v-if="errors.title !== undefined" class="help">
          {{ errors.title }}
        </p>
      </div>
    </div>
    <div class="row">
      <div class="label_container">
        <label>{{ $t("templateEditor.toolbar.link.url.label") }}</label>
      </div>
      <div>
        <input
          type="text"
          class="input"
          v-model="url"
          :class="{ invalid: errors.url !== undefined }"
          placeholder="https://www.pitchview.io"
        />
        <p v-if="errors.url !== undefined" class="help">
          {{ errors.url }}
        </p>
      </div>
    </div>
    <div class="row" v-if="currentLanguage === 'default'">
      <div class="label_container">
        <input type="checkbox" v-model="isMandatory" />
        <label>{{ $t("templateEditor.toolbar.link.isMandatory.label") }}</label>
      </div>
    </div>
    <div class="row" v-if="canReorder">
      <div class="label_container">
        <label>{{ $t("templateEditor.toolbar.link.order.label") }}</label>
      </div>
      <div class="reorder_actions">
        <button
          class="button"
          type="button"
          :disabled="!canMoveUp || reordering"
          :class="{ disabled: !canMoveUp || reordering }"
          @click="changeOrder('down')"
        >
          <span class="material-symbols-outlined"> arrow_upward </span>
          {{ $t("templateEditor.toolbar.link.order.moveUp") }}
        </button>
        <button
          class="button"
          type="button"
          :disabled="!canMoveDown || reordering"
          :class="{ disabled: !canMoveDown || reordering }"
          @click="changeOrder('up')"
        >
          <span class="material-symbols-outlined"> arrow_downward </span>
          {{ $t("templateEditor.toolbar.link.order.moveDown") }}
        </button>
      </div>
    </div>
    <div class="row bottom" v-if="link?.id && currentLanguage === 'default'">
      <button class="button" @click="displayConfirmation">
        <span class="material-symbols-outlined">delete</span>
        {{ $t("templateEditor.toolbar.delete") }}
      </button>
    </div>
  </div>
  <div class="toolbar_footer" @click="submit">
    <div class="button primary" :class="{ disabled: !hasChanges }">
      {{ $t("templateEditor.toolbar.save") }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.toolbar_body {
  padding: 1em;
  font-size: 0.9rem;
  background: var(--classic-surface, #fff);
  height: calc(100vh - 60px - 60px - 82px);
  box-sizing: border-box;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
}

.toolbar_footer {
  padding: 1em;
  font-size: 0.9rem;
  background: var(--classic-surface, #fff);
  border-top: 1px solid var(--classic-border-subtle, #e0e0e0);
}

.material-symbols-outlined {
  font-size: 1.1rem;
  cursor: pointer;
}

.label_container {
  display: flex;
  margin-bottom: 0.5em;
  align-items: center;
}

.label_container label {
  flex: 1;
  font-weight: 500;
}

.label_container .link {
  font-weight: normal;
}

.row {
  margin-bottom: 1.5em;
}

.row.bottom {
  margin-top: auto;
  margin-bottom: 0;
}

.image {
  background: var(--classic-border-subtle, #e0e0e0);
  height: 70px;
}

.image img {
  max-width: 100%;
  max-height: 100%;
}

input[type="checkbox"] {
  margin-right: 0.5em;
}

p.help {
  font-size: 0.8em;
  color: var(--classic-error-text, #f14668);
  margin: 0;
  padding: 0;
  margin-top: 0.25rem;
  height: 0.8em;
}

.reorder_actions {
  display: flex;
  gap: 0.5em;
  flex-wrap: wrap;
}

.reorder_actions .button.disabled,
.reorder_actions .button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.reorder_actions .material-symbols-outlined {
  font-size: 1rem;
}
</style>
